import React, { MouseEventHandler, PropsWithChildren } from 'react';
import bem from '../../../lib/bemUtils/bem';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { isNullOrUndefined } from '../../../lib/typeGuards';

export enum SkipLinkModifier {
  BOTTOM = 'bottom',
}

type SkipLinkProps = {
  id: string;
  modifier?: Modifier<SkipLinkModifier>;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  focusTarget?: boolean;
  stopPropagation?: boolean;
};
/**
 * It's a link that allows to skip content for keyboard users.
 */
export function SkipLink({
  id,
  children,
  onClick,
  modifier,
  focusTarget = false,
  stopPropagation = false,
}: PropsWithChildren<SkipLinkProps>) {
  /**
   * We decided to :focus the SkipLinkTarget manually instead of a :target because in modal dialogs
   * the focusTrap loses focus on :target (focus is on body then) and doesn't set focus
   * to the nearest focusable element after pressing tab again.
   */
  const setFocusToTarget = (event: React.MouseEvent<HTMLAnchorElement>, count: number = 0) => {
    event?.preventDefault();
    const element = document.getElementById(id);
    if (isNullOrUndefined(element)) {
      // wait for navigation to expand, exist and be visible
      if (count <= 15) {
        setTimeout(() => setFocusToTarget(event, count++), 10);
      }
    } else {
      const onBlur = () => {
        element.style.visibility = 'hidden';
        element.removeAttribute('tabindex');
      };

      element.addEventListener('blur', onBlur, { once: true });
      element.setAttribute('tabindex', '-1');
      element.style.visibility = 'visible'; // keep this, otherwise no focus possible!
      element.focus();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    /*
     * preventDefault: Required when focusTarget is set (to avoid browser navigation to #id)
     * or when stopPropagation is set (to prevent the default anchor behavior).
     * We check both conditions because either may require preventing the default action.
     *
     * stopPropagation: Prevents the click event from bubbling up to document-level handlers.
     * This is needed when the onClick handler triggers UI changes (e.g., opening a menu)
     * that would otherwise be immediately closed by a document-level click-outside handler.
     * Without this, elements like the main navigation would flicker open and closed.
     */
    if (focusTarget || stopPropagation) {
      event.preventDefault();
    }
    if (stopPropagation) {
      event.stopPropagation();
    }

    if (onClick) {
      onClick(event);
    }

    /*
     * focusTarget is called after onClick using setTimeout(..., 0) to ensure the React
     * render cycle completes first. Without the timeout, we'd try to focus an element
     * that hasn't been made visible yet by the onClick handler's state change.
     */
    if (focusTarget) {
      setFocusToTarget(event);
    }
  };

  return (
    <a href={`#${id}`} className={bem('skip-link', modifier)} onClick={handleClick}>
      {children}
    </a>
  );
}
